/**
 * Webflow designers place an empty element where Tradewing should render
 * search / notifications / cart / avatar.
 *
 * Default: id="data-tradewing-utilities"
 * Admins can override the id or class name in Webflow Integration settings.
 *
 * Important: TW React utilities are NOT mounted inside this element (Webflow
 * nav CSS uses `all: revert` which would break Chakra menus/icons). The slot
 * is used only as a layout anchor; icons are portaled to document.body and
 * fixed to the slot's bounding box.
 */
export const WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME = 'data-tradewing-utilities';

export type WebflowUtilitiesMountKind = 'ID' | 'CLASS';

export type WebflowUtilitiesMount = {
  kind: WebflowUtilitiesMountKind;
  name: string;
};

export type UtilitiesSlotAnchor = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const sanitizeMountName = (raw: string | null | undefined): string => {
  const trimmed = (raw || '').trim().replace(/^[#.]/, '');
  if (!trimmed || !/^[A-Za-z_][\w-]*$/.test(trimmed)) {
    return WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME;
  }
  return trimmed;
};

export const normalizeUtilitiesMount = (
  kind?: string | null,
  name?: string | null
): WebflowUtilitiesMount => ({
  kind: kind === 'CLASS' ? 'CLASS' : 'ID',
  name: sanitizeMountName(name),
});

const escapeCssIdent = (value: string): string => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/([^\w-])/g, '\\$1');
};

export const findWebflowUtilitiesSlot = (
  container: HTMLElement | null | undefined,
  mount?: WebflowUtilitiesMount | null
): HTMLElement | null => {
  if (!container) return null;

  const resolved = normalizeUtilitiesMount(mount?.kind, mount?.name);
  const safeName = escapeCssIdent(resolved.name);

  if (resolved.kind === 'CLASS') {
    if (container.classList?.contains(resolved.name)) {
      return container;
    }
    return container.querySelector<HTMLElement>(`.${safeName}`);
  }

  if (container.id === resolved.name) {
    return container;
  }

  return (
    container.querySelector<HTMLElement>(`#${safeName}`) ||
    // Legacy attribute / class fallbacks for the default marker only
    (resolved.name === WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME
      ? container.querySelector<HTMLElement>(
          `[${WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME}]`
        ) ||
        container.querySelector<HTMLElement>(
          `.${WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME}`
        )
      : null)
  );
};

/**
 * Reserve space in the Webflow layout for the TW icon cluster and mark the
 * slot so designers can see it in the Designer. Does not host React nodes.
 */
export const prepareWebflowUtilitiesSlot = (slot: HTMLElement): void => {
  slot.setAttribute('data-tradewing-utilities-host', '');
  slot.setAttribute('aria-hidden', 'true');

  const style = slot.style;
  // Keep a stable footprint so the Webflow flex/grid layout doesn't collapse.
  if (!style.minHeight) {
    style.minHeight = '40px';
  }
  if (!style.minWidth) {
    style.minWidth = '180px';
  }
  if (!style.display || style.display === 'none') {
    style.display = 'block';
  }
  // Slot is only an anchor — don't capture clicks meant for the overlay.
  style.pointerEvents = 'none';
};

export const measureUtilitiesSlotAnchor = (
  slot: HTMLElement
): UtilitiesSlotAnchor | null => {
  const rect = slot.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return null;
  }

  return {
    top: rect.top,
    left: rect.left,
    width: Math.max(rect.width, 180),
    height: Math.max(rect.height, 40),
  };
};

/** @deprecated Use WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME */
export const WEBFLOW_UTILITIES_SLOT_ID = WEBFLOW_UTILITIES_SLOT_DEFAULT_NAME;
